import { IconButton, TextField, Tooltip } from "@material-ui/core";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useEffect, useState, useRef } from "react";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GroupIcon from "@mui/icons-material/Group";
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Select from "react-select";
import { toast } from 'react-toastify';
import { trackEvent, debouncedTrackEvent } from "../helpers/trackEvent";

export const FieldNames = {
  firstName: "First name",
  lastName: "Last name",
  companyName: "Company name",
};

const NEXT_ELEMENT_SHIFT = 50;

export const PDFControls = ({
  fields,
  setFields,
  updateFields = setFields,
  printSamplePDF,
  printStaticPDF,
  onPrintMultipleBadgesClick,
  onFieldFocus,
  onFieldBlur,
  onFieldAdd,
  isTemplateModalOpen,
  importedSpreadsheetData,
  setImportedSpreadsheetData,
  onReimportClick,
  attendeeSelectionResetKey = 0,
  elementsCount,
  setElementsCount,
  setNewFieldName,
  paymentsEnabled = false,
  showPurchasedBadgesButton = false,
  onPurchasedBadgesClick,
  readOnly = false,
}) => {
  const [selectedAttendeeIndex, setSelectedAttendeeIndex] = useState(0);
  const buttonRef = useRef(null);

  useEffect(() => {
    setSelectedAttendeeIndex(0);
  }, [attendeeSelectionResetKey]);

  useEffect(() => {
    const attendeeCount = importedSpreadsheetData?.fileData?.data?.length || 0;

    if (attendeeCount === 0) {
      setSelectedAttendeeIndex(0);
      return;
    }

    if (selectedAttendeeIndex > attendeeCount - 1) {
      setSelectedAttendeeIndex(attendeeCount - 1);
    }
  }, [importedSpreadsheetData, selectedAttendeeIndex]);

  // Handle attendee selection change
  const handleAttendeeChange = (selectedOption) => {
    const newIndex = selectedOption?.value;

    if (newIndex === undefined || newIndex === selectedAttendeeIndex) {
      return;
    }

    trackEvent({
      action: 'preview_attendee_changed',
      data: {
        attendeeIndex: newIndex,
        attendeesCount: importedSpreadsheetData?.fileData?.data?.length || 0,
      },
    });

    setSelectedAttendeeIndex(newIndex);

    if (importedSpreadsheetData) {
      const { mapping, fileData } = importedSpreadsheetData;
      const selectedRow = fileData.data[newIndex] || [];

      // Update field values based on the selected attendee
      const updatedFields = { ...fields };
      Object.entries(mapping).forEach(([header, fieldName]) => {
        const columnIndex = fileData.headers.indexOf(header);
        if (columnIndex !== -1 && updatedFields[fieldName]) {
          const value = selectedRow[columnIndex];
          updatedFields[fieldName] = {
            ...updatedFields[fieldName],
            value: value != null ? String(value).trim().replace(/ /g, '\u00A0') : "",
            focused: false,
          };
        }
      });
      updateFields(updatedFields, { suppressTemplateSave: true });
    }
  };

  // Navigate to previous attendee
  const handlePreviousAttendee = () => {
    if (importedSpreadsheetData && selectedAttendeeIndex > 0) {
      handleAttendeeChange({ value: selectedAttendeeIndex - 1 });
    }
  };

  // Navigate to next attendee
  const handleNextAttendee = () => {
    if (importedSpreadsheetData && selectedAttendeeIndex < importedSpreadsheetData.fileData.data.length - 1) {
      handleAttendeeChange({ value: selectedAttendeeIndex + 1 });
    }
  };

  // Get attendee display names
  const getAttendeeDisplayName = (rowData, index) => {
    if (!importedSpreadsheetData) return `Attendee ${index + 1}`;
    if (!rowData) return `Attendee ${index + 1}`;

    const { mapping, fileData } = importedSpreadsheetData;

    // Try to find Full name field
    const fullNameHeader = Object.keys(mapping).find(header => mapping[header] === "Full name");
    if (fullNameHeader) {
      const columnIndex = fileData.headers.indexOf(fullNameHeader);
      const fullName = rowData[columnIndex];
      if (fullName) return String(fullName).trim();
    }

    // Try to build from First name and Last name
    const firstNameHeader = Object.keys(mapping).find(header => mapping[header] === "First name");
    const lastNameHeader = Object.keys(mapping).find(header => mapping[header] === "Last name");

    if (firstNameHeader || lastNameHeader) {
      const firstName = firstNameHeader ? rowData[fileData.headers.indexOf(firstNameHeader)] : '';
      const lastName = lastNameHeader ? rowData[fileData.headers.indexOf(lastNameHeader)] : '';
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      if (fullName) return fullName;
    }

    return `Attendee ${index + 1}`;
  };

  return (
    <div className="pdf-controls">
      <div className="pdf-controls-top" data-tour="pdf-controls-top">
        <div className="pdf-controls-header">
          <div className="pdf-controls-header-content">
            <div className="pdf-controls-title">
              <PersonIcon className="pdf-controls-icon" />
              <div className="pdf-controls-heading">Attendee info</div>
            </div>
          </div>
          <div className="pdf-controls-description">
            Select an attendee to preview their badge
          </div>
        </div>
        {importedSpreadsheetData && importedSpreadsheetData.fileData.data.length > 0 && (
          <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title="Previous attendee">
              <span>
                <IconButton
                  size="small"
                  onClick={handlePreviousAttendee}
                  disabled={selectedAttendeeIndex === 0}
                  sx={{ padding: '4px' }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </span>
            </Tooltip>
            <div style={{ flex: 1 }}>
              <Select
                className="attendee-select"
                value={{
                  value: selectedAttendeeIndex,
                  label: getAttendeeDisplayName(importedSpreadsheetData.fileData.data[selectedAttendeeIndex], selectedAttendeeIndex)
                }}
                onChange={handleAttendeeChange}
                options={importedSpreadsheetData.fileData.data.map((rowData, index) => ({
                  value: index,
                  label: getAttendeeDisplayName(rowData, index)
                }))}
                placeholder="Select Attendee"
                isSearchable={true}
              />
            </div>
            <Tooltip title="Next attendee">
              <span>
                <IconButton
                  size="small"
                  onClick={handleNextAttendee}
                  disabled={selectedAttendeeIndex === importedSpreadsheetData.fileData.data.length - 1}
                  sx={{ padding: '4px' }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </div>
      <div className="pdf-controls-fields" data-tour="pdf-controls-fields">
        {Object.entries(fields).map(([label, { value, enabled }]) => (
          <div key={label} className="input-container">
            <TextField
              margin="dense"
              label={label}
              fullWidth
              value={value}
              disabled={readOnly || !enabled}
              onFocus={() => onFieldFocus(label)}
              onBlur={() => onFieldBlur(label)}
              onChange={(e) => {
                const newValue = e.target.value.replace(/ /g, '\u00A0');
                setFields(
                  Object.fromEntries(
                    Object.entries(fields).map(([key, field]) => [
                      key,
                      key === label
                        ? { ...field, value: newValue, focused: true }
                        : { ...field, focused: false }
                    ])
                  )
                );

                // Update importedSpreadsheetData if it exists
                if (importedSpreadsheetData) {
                  const { mapping, fileData } = importedSpreadsheetData;

                  // Find the header that maps to this field label
                  const header = Object.keys(mapping).find(h => mapping[h] === label);

                  if (header) {
                    const columnIndex = fileData.headers.indexOf(header);

                    if (columnIndex !== -1) {
                      // Update the value in the current attendee's row
                      const updatedData = [...fileData.data];
                      updatedData[selectedAttendeeIndex] = [...updatedData[selectedAttendeeIndex]];
                      updatedData[selectedAttendeeIndex][columnIndex] = newValue.replace(/\u00A0/g, ' ');

                      setImportedSpreadsheetData({
                        ...importedSpreadsheetData,
                        fileData: {
                          ...fileData,
                          data: updatedData,
                        },
                      });

                      // toast.success(`Updated ${label} for ${getAttendeeDisplayName(fileData.data[selectedAttendeeIndex], selectedAttendeeIndex)}`, {
                      //   position: "bottom-right",
                      //   autoClose: 2000,
                      //   hideProgressBar: false,
                      //   closeOnClick: true,
                      //   pauseOnHover: true,
                      //   draggable: true,
                      // });
                    }
                  }
                }

                debouncedTrackEvent({
                  action: 'change_field_value'
                })
              }}
            />
            <Tooltip title={enabled ? "Hide field from badge" : "Show field on badge"}>
              <IconButton
                disabled={readOnly}
                size="small"
                sx={{ padding: '4px' }}
                onClick={() => {
                  const field = fields[label];
                  const isEnabling = !enabled;

                  if (isEnabling) {
                    setNewFieldName(label);
                  }

                  // If enabling a field that doesn't have stored props (e.g. width), initialize them
                  if (isEnabling && !field.width) {
                    setFields({
                      ...fields,
                      [label]: {
                        ...field,
                        enabled: true,
                        type: field.type || "text",
                        left: 50 + elementsCount.text * NEXT_ELEMENT_SHIFT,
                        top: 50 + elementsCount.text * NEXT_ELEMENT_SHIFT,
                        fontSize: field.fontSize || 50,
                        fontWeight: field.fontWeight || "bold",
                        fontFamily: field.fontFamily || "Josefin Sans",
                        element: null,
                        focused: true,
                      },
                    });

                    setElementsCount({
                      ...elementsCount,
                      text: elementsCount.text + 1,
                    });
                  } else {
                    // Just toggle enabled state
                    setFields({
                      ...fields,
                      [label]: {
                        ...field,
                        enabled: !enabled,
                        focused: isEnabling,
                      },
                    });
                  }
                }}
              >
                {enabled ? <VisibilityOffIcon sx={{ fontSize: '22px' }} /> : <VisibilityIcon sx={{ fontSize: '22px' }} />}
              </IconButton>
            </Tooltip>
          </div>
        ))}
        {!Object.entries(fields).length && (
          <div className="empty-fields-placeholder">
            <FormatListBulletedIcon className="empty-fields-icon" />
            <div className="empty-fields-text">No variable data fields added</div>
          </div>
        )}
      </div>
      <div className="pdf-controls-buttons">
        {/* {Object.entries(fields).length ? (
          <button className="button" onClick={() => {
            trackEvent({
              action: 'click_download_sample_badges'
            })
            printSamplePDF();
          }}>
            <VisibilityIcon className="button-icon" />
            Generate sample
          </button>
        ) : (
          <button className="button" onClick={() => {
            trackEvent({
              action: 'click_download_static_badges'
            })
            printStaticPDF()
          }}>
            <PrintIcon className="button-icon" />
            Generate static badges
          </button>
        )} */}
        {importedSpreadsheetData && importedSpreadsheetData.fileData.data.length > 0 && (
          <div className="imported-badges-summary">
            <div className="imported-badges-count">
              {importedSpreadsheetData.fileData.data.length} attendee{importedSpreadsheetData.fileData.data.length === 1 ? '' : 's'} imported
            </div>
            {onReimportClick && !readOnly && (
              <button
                className="button link-button reimport-attendees-button"
                type="button"
                onClick={onReimportClick}
              >
                Reimport
              </button>
            )}
          </div>
        )}
        <div style={{
          position: 'relative', display: 'grid'
        }}>
          <button
            ref={buttonRef}
            className="primary"
            data-tour="pdf-controls-download"
            disabled={!Object.entries(fields).length
            }
            onClick={() => {
              trackEvent({
                action: 'click_download_attendees_badges'
              })
              if (Object.entries(fields).length) {
                onPrintMultipleBadgesClick();
              }
            }}
          >
            <DownloadIcon className="button-icon" />
            {paymentsEnabled
              ? "Download badges"
              : importedSpreadsheetData && importedSpreadsheetData.fileData.data.length > 0
              ? `Download ${importedSpreadsheetData.fileData.data.length} badge${importedSpreadsheetData.fileData.data.length === 1 ? '' : 's'}`
              : 'Generate attendee badges'
            }
          </button>
          {paymentsEnabled && showPurchasedBadgesButton && (
            <button
              className="button purchased-badges-button"
              type="button"
              onClick={onPurchasedBadgesClick}
            >
              Get purchased badges
            </button>
          )}
        </div>
      </div>
    </div >
  );
};
